import React, { useState, useEffect, useRef } from 'react'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { CreateSupplier, Supplier, Province, axios } from '@getgreenline/homi-shared'
import { debounce } from 'lodash'
import { CreateProduct } from '../ProductStore'
import { SetProductNameModal } from '../Components/SetProductNameModal'
import {
  message,
  Modal,
  Skeleton,
  Input,
  Button,
  Icon,
  Alert,
  Checkbox,
  Tooltip,
  Empty,
} from 'antd'
import { BarcodeListener } from '../../../../../components/BarcodeListener'
import { ImagePlaceholder } from '../../../../../components/Placeholder/ImagePlaceholder'
import { observer, inject } from 'mobx-react'
import { Track } from '../../../../../Track'
import { parseProvinceProductAgency } from '../../../../../utilities/helpers'
import '../ImportProvinceProduct.scss'
import { BLAZE_COMPANY } from '../../../../../constants/general'
import { HelpLinks } from '../../../../../constants/ExternalLinks'
import { IGlobalProduct } from '@getgreenline/global-products'
import Endpoints from '../../../../../constants/Endpoints'
import { ImportGlobalProductsPreview } from '../Components/ImportGlobalProductsPreview'
import { getSkuFromGlobalProductV2 } from '../../../../../utilities/globalProduct'

interface AGLCProductProps {
  onAddProduct: (item: IGlobalProduct) => void
  currentCompanyStore?: CurrentCompanyStore
  item: IGlobalProduct
  province: Province | null
}

export const ProvinceProductRow = inject('currentCompanyStore')(
  React.memo((props: AGLCProductProps) => {
    const product = props.item
    const province = props.province

    return (
      <div className='mb-2 mt-2'>
        <div className='d-flex'>
          <div className='mr-2' style={{ flex: 'none', width: 80, height: 80 }}>
            {product.images.length ? (
              <img
                className='border border-light'
                src={product.images[0] || ''}
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <ImagePlaceholder style={{ width: '100%', height: '100%' }} />
            )}
          </div>
          <div className='mr-2' style={{ flexGrow: 1 }}>
            <div>
              <b>{product.name}</b>
            </div>
            <div className='blaze-text-muted'>
              <small>SKU: {getSkuFromGlobalProductV2(product)}</small>
            </div>
            <div className='blaze-text-muted'>
              <small>Barcode: {product.barcode}</small>
            </div>
            <div className='blaze-text-muted'>
              <small>
                {'Brand: '}
                {province !== null && ['AB', 'BC'].includes(province)
                  ? product.supplier
                  : product.brandName}
              </small>
            </div>
            <div className='blaze-text-muted'>
              <small>{product.categories.join(', ')}</small>
            </div>
          </div>
          <div className='pt-4'>
            <Button onClick={() => props.onAddProduct(props.item)} style={{ marginRight: 5 }}>
              Add <span className='fa fa-caret-right ml-2' />
            </Button>
          </div>
        </div>
        <hr />
      </div>
    )
  }),
)

interface Props {
  product: CreateProduct
  currentCompanyStore?: CurrentCompanyStore
  activeKeys: string[]
  setActiveKeys: (activeKeys: string[]) => void
  populateGlobalProduct: (products: IGlobalProduct[], brandId?: number) => void
  setExpanded: (status: boolean) => void
  autoScrollToVariants: () => void
}

export const ImportGlobalProduct = inject('currentCompanyStore')(
  observer(
    React.memo(
      ({
        product,
        currentCompanyStore,
        activeKeys,
        setActiveKeys,
        populateGlobalProduct,
        setExpanded,
        autoScrollToVariants,
      }: Props) => {
        const { province } = currentCompanyStore!.company!
        const canUseGlobalCatalog = true // props.currentCompanyStore?.canUseGlobalCatalog

        const [confirm, showConfirm] = useState(false)
        const [isLoading, setLoadingState] = useState(false)
        const [isScanning, setScanningState] = useState(false)
        const [childProductsToAdd, setChildProductsToAdd] = useState<IGlobalProduct[]>([])
        const [provinceProductList, setProvinceProducts] = useState<IGlobalProduct[]>([])
        const [addVendors, setAddVendors] = useState(true)
        const inputRef = useRef('')
        const isMasterProduct = product.isMasterProduct
        const [currentSupplierId, setCurrentBrandId] = useState<number | null>(null)

        const onRemove = (productIndex: number) => {
          const newChildProductList = childProductsToAdd.filter(
            (productItem, n) => productIndex !== n,
          )
          setChildProductsToAdd(newChildProductList)
        }

        const addProduct = async (item?: IGlobalProduct | null) => {
          if (!item) return
          const brandName = province === 'ON' ? item.brandName : item.supplier
          if (brandName) {
            const existingBrand = checkForBrand(brandName)
            const brandId = existingBrand ? existingBrand.id : await confirmBrandCreation(brandName)
            setCurrentBrandId(brandId || null)
          }

          setChildProductsToAdd(isMasterProduct ? [...childProductsToAdd, item] : [item])
        }

        async function confirmBrandCreation(brandName: string) {
          if (addVendors) {
            try {
              if (
                window.confirm(
                  `The brand ${brandName} does not exist. Would you like to have it generated for you?`,
                )
              ) {
                const brand = new CreateSupplier()
                brand.name = brandName
                const newBrand = await currentCompanyStore?.supplierStore?.addSupplier(brand)
                Track.track('dashboard:products_provinceImportVendorPrompt', { type: 'accept' })
                return newBrand?.id
              } else {
                Track.track('dashboard:products_provinceImportVendorPrompt', { type: 'reject' })
                return
              }
            } catch (error) {
              console.error(error)
              message.error(`There was a problem creating the brand ${brandName}, please try again`)
            }
          }
        }

        const checkForBrand = (brand?: string): Supplier | undefined => {
          const matchingBrand = currentCompanyStore?.supplierStore?.suppliers?.find(
            ({ name }) => name?.toLowerCase()?.trim() === brand?.toLowerCase()?.trim(),
          )
          return matchingBrand
        }

        const searchGlobalProducts = async (searchQuery = '') => {
          if (!searchQuery) return setProvinceProducts([])
          if (searchQuery.length < 3) return
          if (!province) return
          try {
            setLoadingState(true)
            const response = await axios.get<{ products: IGlobalProduct[] }>(
              Endpoints.SEARCH_GLOBAL_PRODUCTS(),
              {
                params: {
                  query: searchQuery,
                  limit: 100,
                  offset: 0,
                },
              },
            )
            setProvinceProducts(response.data.products)
          } catch (error) {
            console.error(error)
            message.error(`There was a problem searching for global products, please try again`)
          } finally {
            setLoadingState(false)
          }
        }

        const checkProductExists = (item: IGlobalProduct) => {
          if (!item) return false
          const itemSku = getSkuFromGlobalProductV2(item)
          const childProductSkus = new Set(
            childProductsToAdd.map((childProduct) => getSkuFromGlobalProductV2(childProduct)),
          )
          const existingVariantSkus = new Set(
            product.childProducts.filter(({ sku }) => sku).map(({ sku }) => sku),
          )
          return childProductSkus.has(itemSku) || existingVariantSkus.has(itemSku)
        }

        const onBarcodeEnter = async (value: string) => {
          await searchGlobalProducts(value)
          const productToAdd = provinceProductList[0]

          const doesProductExist = checkProductExists(productToAdd)

          if (!doesProductExist) {
            addProduct(productToAdd)
          } else {
            message.error(`SKU already exists`)
          }
          Track.track('dashboard:products_provinceImportBarcodeScanned', { value: value })
          setScanningState(false)
        }

        const onImport = () => {
          const newProductIndexes = Array.from({ length: childProductsToAdd.length }, (_, i) =>
            (i + product.childProducts.length).toString(),
          )
          setActiveKeys([...activeKeys, ...newProductIndexes])

          product.setSupplierId(currentSupplierId)

          populateGlobalProduct(childProductsToAdd, product.supplierId || undefined)
          Track.track('dashboard:products_provinceImported', {
            numberOfProducts: childProductsToAdd.length,
          })
          message.success(`Successfully imported variants`)
          setExpanded(false)
          setChildProductsToAdd([])
          autoScrollToVariants()
        }

        const debounceTime = 600
        const loadProductsDebounced = debounce(
          (inputValue: string) => searchGlobalProducts(inputValue),
          debounceTime,
        )
        const filterProductList = (itemList: IGlobalProduct[]) => {
          const variantBarcodes = product.childProducts.map((variant) => variant.barcode)
          const productsToAddUniqueKeys = childProductsToAdd.map(
            (childProduct) => childProduct.barcode,
          )

          return itemList.filter(
            (item) =>
              !productsToAddUniqueKeys.includes(item.barcode) &&
              !variantBarcodes.includes(item.barcode),
          )
        }
        let filteredProducts = filterProductList(provinceProductList)

        useEffect(() => {
          filteredProducts = filterProductList(provinceProductList)
        }, [provinceProductList, childProductsToAdd])

        const noItemsMessage = (
          <Alert
            type='warning'
            showIcon
            message='No matching product'
            description={
              <>
                <p>This product may be new and not yet updated in our catalog.</p>
                <p>
                  {BLAZE_COMPANY} relies on product data sent by stores to keep the catalog up to
                  date. Please send the latest files to{' '}
                  <a href='support-ca@blaze.me'>support-ca@blaze.me</a>
                </p>
                Otherwise, product variants can be manually created.
              </>
            }
          />
        )

        const isProductToAdd = childProductsToAdd.length > 0

        const renderImportButtonText = (): string => {
          switch (true) {
            case isMasterProduct && isProductToAdd:
              return `Import ${childProductsToAdd.length} variant${
                childProductsToAdd.length === 1 ? '' : 's'
              }`
            case !isMasterProduct && isProductToAdd:
              return 'Import product'
            case !isMasterProduct && !isProductToAdd:
              return 'No variants to import'
            default:
              return 'No product to import'
          }
        }

        const modalTitle = `Import ${
          isMasterProduct ? 'variant ' : ''
        }from ${parseProvinceProductAgency(canUseGlobalCatalog ? null : province)}`

        return (
          <>
            <Modal
              title={modalTitle}
              width={798}
              visible
              maskClosable={false}
              onCancel={() => setExpanded(false)}
              footer={null}
            >
              <div className='row'>
                <div className='col-md-8'>
                  <div className='d-flex justify-content-between'>
                    <div className='mr-2' style={{ flexGrow: 1 }}>
                      <Input
                        name='province product search'
                        placeholder={`Search by name${
                          province !== null && ['AB', 'BC', 'ON'].includes(province) ? ', SKU,' : ''
                        } or barcode`}
                        onChange={(e) => {
                          loadProductsDebounced(e.target.value)
                          inputRef.current = e.target.value
                        }}
                      />
                    </div>
                    <div className='mr-2'>
                      <Button
                        type={isScanning ? 'primary' : 'default'}
                        onClick={() => setScanningState(!isScanning)}
                      >
                        <Icon type='barcode' style={{ marginRight: 4 }} />
                        {isScanning ? 'Scanning...' : 'Scan'}
                      </Button>
                    </div>
                  </div>

                  <div className='mt-2 mb-4'>
                    <Checkbox checked={addVendors} onChange={() => setAddVendors(!addVendors)}>
                      <span className='mr-2'>Prompt to add brands</span>
                      <Tooltip
                        placement='right'
                        title={
                          <>
                            <p>
                              If the catalog brand name does not exist, you will be asked if you
                              want it automatically created.
                            </p>
                            <p className='mb-0'>
                              Turn off if you want your own brand naming conventions.
                            </p>
                          </>
                        }
                      >
                        <Icon type='info-circle' />
                      </Tooltip>
                    </Checkbox>
                  </div>

                  {isScanning && <BarcodeListener onBarcodeEnter={onBarcodeEnter} />}

                  {provinceProductList.length > 0 ? (
                    <div style={{ maxHeight: '45vh', overflowX: 'auto' }}>
                      {filteredProducts.slice(0, 50).map((item, index) => {
                        return (
                          <ProvinceProductRow
                            onAddProduct={addProduct}
                            item={item}
                            province={province}
                            key={`ProductList-${
                              item.regionalData?.find(
                                (region) => region.region === province?.toUpperCase(),
                              )?.sku
                            }-${item.barcode}-${index}`}
                          />
                        )
                      })}
                    </div>
                  ) : isLoading ? (
                    <Skeleton active />
                  ) : (
                    <div className='mt-4 mb-2'>
                      {inputRef.current !== '' ? (
                        noItemsMessage
                      ) : (
                        <Empty
                          description={
                            <>
                              Search for a product
                              <br />
                              Learn more{' '}
                              <a
                                href={HelpLinks.PRODUCTS_ADD_FROM_DB}
                                target='_blank'
                                rel='noreferrer'
                              >
                                here
                              </a>
                            </>
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
                <div className='col-md-4'>
                  {!isProductToAdd ? (
                    <div className='blaze-text-muted mb-2'>
                      <ol style={{ paddingLeft: 20 }}>
                        <li>Find the variants you want to add</li>
                        <li>Complete the import</li>
                        <li>Add SKU, categories, taxes, and prices</li>
                        <li>Save</li>
                      </ol>
                    </div>
                  ) : (
                    <ImportGlobalProductsPreview
                      products={childProductsToAdd}
                      handleRemove={onRemove}
                      currentCompanyStore={currentCompanyStore}
                    />
                  )}

                  <Button
                    type='primary'
                    onClick={() => (isMasterProduct ? showConfirm(true) : onImport())}
                    disabled={!isProductToAdd}
                    style={{ width: '100%', marginTop: 5 }}
                  >
                    {renderImportButtonText()}
                  </Button>
                </div>
              </div>

              {confirm && isMasterProduct && (
                <SetProductNameModal
                  onClose={() => showConfirm(false)}
                  product={product}
                  onImport={onImport}
                />
              )}
            </Modal>
          </>
        )
      },
    ),
  ),
)
